package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.about.styles.AboutValuesContainerStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.components.widgets.YouTubeVideoCard
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Storybook Reveal
@Composable
private fun Modifier.storybookReveal(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.95f)
        .rotate(if (isVisible) 0.deg else (-3).deg).transition(
            Transition.of("opacity", 0.9.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.9.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
private fun Modifier.slideRightFade(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else (-40).px).transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun AboutOriginSection(data: AboutOriginSectionData = defaultAboutOriginData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-origin-section"
    var isVisible by remember { mutableStateOf(false) }

    Column(
        modifier = AboutValuesContainerStyle.toModifier().id(targetId).fillMaxWidth().backgroundColor(Colors.White)
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
            .styleModifier { property("scroll-margin-top", "100px") },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.sectionTitle.isNotBlank()) {
                    SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 25.px).slideRightFade(isVisible, 0),
                        alignment = Alignment.CenterHorizontally
                    )
                }

                if (data.shortIntro.isNotBlank()) {
                    Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(900.px).fontSize(20.px).lineHeight(1.6)
                            .fontWeight(FontWeight.Medium).color(palette.brand.primary).textAlign(TextAlign.Center)
                            .margin(bottom = 40.px).slideRightFade(isVisible, 150)
                            .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.shortIntro; onDispose {} } })
                }

                if (data.founderVideoEmbedUrl.isNotBlank()) {
                    Box(
                        modifier = Modifier.fillMaxWidth()
                            .maxWidth(if (data.founderVideoFormat == VideoFormat.CINEMATIC_ULTRAWIDE) 1440.px else 1000.px)
                            .storybookReveal(isVisible, 300), contentAlignment = Alignment.Center
                    ) {
                        YouTubeVideoCard(
                            embedUrl = data.founderVideoEmbedUrl,
                            thumbnailUrl = data.founderVideoThumbnailUrl,
                            ariaLabel = data.founderVideoAriaLabel,
                            labelPillText = data.founderVideoLabelPillText,
                            videoFormat = data.founderVideoFormat,
                            modifier = Modifier.fillMaxWidth().boxShadow(
                                0.px,
                                20.px,
                                40.px,
                                color = palette.custom.darkText.toRgb().copyf(alpha = 0.15f)
                            )
                        )
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS CONTENT LIMITS FOR ALIGNMENT:
 * To ensure the text column perfectly aligns with the 16:9 video on desktop screens
 * without causing awkward vertical gaps, strict text limits must be enforced:
 * - Max Characters (Total across all paragraphs): 450 characters
 * - Max Words (Total across all paragraphs): ~70 words
 * - Max Paragraphs: 2
 * * CMS IMAGE UPLOAD SPECS FOR ABOUT ORIGIN:
 *
 * @field imagePath:
 * - Aspect Ratio: 4:3 (Standard Photo)
 * - Recommended Dimensions: 1200x900px.
 * - Format: WebP or high-quality JPEG.
 * * @field founderVideoEmbedUrl (OPTIONAL): A `https://www.youtube.com/embed/<ID>` URL.
 */
data class AboutOriginSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Our Founder's Story",
    val shortIntro: String = "After 25 years as a Physiotherapist & Neuroscientist, our Founder & CEO, Vineet Johnson saw a frustrating and unfortunate reality: despite all medical advancements traditional therapies fail stroke survivors, leaving them with lifelong hand disabilities.\n\nIRegained was founded to bridge this exact gap – combining deep neuroscience research with precision engineering and intelligent algorithms for a complete Digital Neurotherapeutic(DTx) solution. His foundational neuroscience research and clinical expertise and the drive to bring forth positive social impact, is empowering patients globally to regain their lost hand function, and restore their quality of life.",
    // Extended Story: The full deep-dive. Formatted with standard HTML <p> tags.
    val founderVideoEmbedUrl: String = Res.Strings.Links.FOUNDER_STORY,
    val founderVideoThumbnailUrl: String = Res.Images.About.Origin.ORIGIN_IMG,
    val founderVideoLabelPillText: String? = "Watch · Our Founder's Story",
    val founderVideoAriaLabel: String = "Play the IRegained founder story video",
    val founderVideoFormat: VideoFormat = VideoFormat.STANDARD_16_9
)

val defaultAboutOriginData = AboutOriginSectionData()